import { useEffect, useRef } from "react";
import { motion } from "motion/react";
import logoImage from "figma:asset/logo.png";
import type { BlockGeneratedCelebration } from "./celebration.types";
import { buildBlockBadgeProgress } from "./blockBadgeProgress";
import { getCelebrationDurationMs } from "./celebrationConfetti";
import { clearWindowTimer } from "./celebrationTimers";

type MinimalBlockCelebrationProps = {
  celebration: BlockGeneratedCelebration;
  onDismiss: () => void;
  shouldReduceMotion: boolean;
};

export function MinimalBlockCelebration({
  celebration,
  onDismiss,
  shouldReduceMotion,
}: MinimalBlockCelebrationProps) {
  const timerRef = useRef<number | null>(null);
  const isHoveringRef = useRef(false);
  const hasTimerExpiredRef = useRef(false);
  const badgeProgress = celebration.badgeProgress
    ? buildBlockBadgeProgress(celebration.badgeProgress)
    : null;
  const title = badgeProgress?.headline ?? celebration.title ?? "+1 Block";
  const message = badgeProgress?.detail ?? celebration.message ?? "Progress saved";
  const rewardLabel = badgeProgress
    ? `+${badgeProgress.rewardBlocks.toLocaleString()} ${
        badgeProgress.rewardBlocks === 1 ? "Block" : "Blocks"
      }`
    : null;

  useEffect(() => {
    hasTimerExpiredRef.current = false;
    isHoveringRef.current = false;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      hasTimerExpiredRef.current = true;
      if (!isHoveringRef.current) {
        onDismiss();
      }
    }, getCelebrationDurationMs(celebration));

    return () => clearWindowTimer(timerRef);
  }, [celebration, onDismiss]);

  return (
    <motion.div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-background/12 backdrop-blur-[2px]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: shouldReduceMotion ? 0.01 : 0.16 }}
      onPointerDown={onDismiss}
    >
      <motion.div
        role="status"
        aria-live="polite"
        className="flex w-[min(22rem,calc(100vw-2rem))] flex-col items-center gap-3 rounded-lg border border-border/70 bg-card/88 px-7 py-6 text-center shadow-2xl shadow-black/12 backdrop-blur-md"
        onPointerDown={(event) => event.stopPropagation()}
        onPointerEnter={() => {
          isHoveringRef.current = true;
        }}
        onPointerLeave={() => {
          isHoveringRef.current = false;
          if (hasTimerExpiredRef.current) {
            onDismiss();
          }
        }}
        initial={
          shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 10, scale: 0.96 }
        }
        animate={
          shouldReduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }
        }
        exit={
          shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }
        }
        transition={
          shouldReduceMotion
            ? { duration: 0.01 }
            : { type: "spring", stiffness: 360, damping: 30, mass: 0.7 }
        }
      >
        <div className="flex size-14 items-center justify-center rounded-lg border border-border/70 bg-background/90 p-2 shadow-sm">
          <img src={logoImage} alt="" className="size-full object-contain" />
        </div>
        <div className="space-y-1">
          {rewardLabel ? (
            <p className="mx-auto w-fit rounded-full border border-border/70 bg-muted/50 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
              {rewardLabel}
            </p>
          ) : null}
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{message}</p>
        </div>
        {badgeProgress?.nextBadgeName ? (
          <div className="w-full space-y-2">
            <div className="flex items-center justify-between gap-3 text-[11px] font-medium text-muted-foreground">
              <span className="truncate">
                {badgeProgress.currentBadgeName ?? badgeProgress.previousBadgeName}
              </span>
              <span className="truncate text-foreground">
                {badgeProgress.nextBadgeName}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-linear-to-r from-emerald-500 via-amber-400 to-blue-500"
                style={{ width: `${badgeProgress.progressPercent}%` }}
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              {badgeProgress.progressLabel}
            </p>
          </div>
        ) : null}
      </motion.div>
    </motion.div>
  );
}
